
import FolderZipIcon from '@mui/icons-material/FolderZip';
import { Button, TextField, Typography } from "@mui/material";
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { EventPhoto, OrderWithItems } from 'common-models';

import { FC, useEffect, useState } from "react";
import { Link, useParams } from 'react-router-dom';
import { Configuration } from 'src/Configuration';
import { EventGalleryPhoto } from 'src/components/events/EventGalleryPhoto';
import PageIsLoading from "../../components/PageIsLoading";
import { useGetOrderQuery, useGetPhotosForOrderQuery, useSendCompletedOrderMailMutation } from '../../features/events/eventShopApiSlice';
import { useLazyGetPhotoDownloadUrlQuery, useLazyGetSingleEventQuery } from '../../features/events/eventsApiSlice';
import { useLazyGetImagesZipFileForOrderQuery } from '../../features/events/zipDownloadApiSlice';

interface EventWithPhotos {
    eventName: string;
    eventStartTime: Date;
    eventEndTime: Date;
    photos: Array<EventPhoto>;
}


const OrdersPage: FC<any> = () => {
    const params = useParams();
    const orderId = params.orderId as string;

    const { data: orderFromApi = undefined as unknown as OrderWithItems } = useGetOrderQuery(orderId);
    const { data: photosFromApi = undefined as unknown as Array<EventPhoto> } = useGetPhotosForOrderQuery(orderId);
    const [getImagesForZipFile, { data: zipFileData, isLoading: zipFileIsLoading }] = useLazyGetImagesZipFileForOrderQuery();


    const [sendOrderCompleteEmail] = useSendCompletedOrderMailMutation();
    const [getDownloadUrl] =
        useLazyGetPhotoDownloadUrlQuery({});

    const [getSingleEvent] =
        useLazyGetSingleEventQuery({});

    const [order, setOrder] = useState(undefined as unknown as OrderWithItems);
    const [eventsWithPhotos, setEventsWithPhotos] = useState([] as Array<EventWithPhotos>);

    const [zipFileDownloadButtonText, setZipFileDownloadButtonText] = useState(<>...</>);

    useEffect(() => {
        if (orderFromApi !== undefined) {
            setOrder(orderFromApi);
        }
    }, [orderFromApi])

    const [emailAddress, setEmailAddress] = useState('');
    const [emailIsValid, setEmailIsValid] = useState(false);


    useEffect(() => {
        setZipFileDownloadButtonText(zipFileIsLoading ? <><PageIsLoading /> Ladda ned alla bilder som en zip-fil</> : <><FolderZipIcon /> Ladda ned alla bilder som en zip-fil</>)
    }, [zipFileIsLoading])
    useEffect(() => {
        setEmailIsValid(isValidEmail(emailAddress))
    }, [emailAddress])

    useEffect(() => {
        async function loadPhotosData() {
            const eventIds: Array<string> = [];
            for (const orderItem of order.items) {
                if (!eventIds.includes(orderItem.eventId)) {
                    eventIds.push(orderItem.eventId);
                }
            }

            const result: Array<EventWithPhotos> = [];
            for (const eventId of eventIds) {
                const response = await getSingleEvent({ eventId: eventId });
                if (response.isSuccess) {
                    result.push({
                        eventName: response.data.title,
                        eventStartTime: new Date(response.data.eventStartTime),
                        eventEndTime: new Date(response.data.eventEndTime),
                        photos: photosFromApi.filter(x => x.eventId === eventId)
                    })
                }
            }

            setEventsWithPhotos(result);
        }
        if (order && photosFromApi) {
            loadPhotosData();
        }
    }, [getSingleEvent, order, photosFromApi])

    const isValidEmail = (email: string) => {
        return /\S+@\S+\.\S+/.test(email);
    }

    const onEmailAddressChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setEmailAddress(event.target.value);
    }

    const sendMail = async () => {
        await sendOrderCompleteEmail({ orderId: orderId, emailAddress: emailAddress });
    }

    const invokeFileDownload = (url: string, filename: string) => {
        console.info(`Invoking download for url ${url}`);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
    }



    const downloadImage = async (eventId: string, imageId: string) => {
        const response = await getDownloadUrl({
            eventId: eventId,
            imageId: imageId,
        });

        if (response.isSuccess) {
            const url = response.data;
            invokeFileDownload(url, `${imageId}.jpg`);
        }
    }

    const downloadZipFile = async () => {
        await getImagesForZipFile(orderId);
        if (zipFileData !== undefined) {
            const downloadUrl = URL.createObjectURL(zipFileData);
            invokeFileDownload(downloadUrl, `eventbild-${orderId}.zip`);
        }

    }



    return (
        <Root>
            {order === undefined ? (
                <PageIsLoading />
            ) : (
                <>
                    <Grid container >
                        <Typography variant='h1'>Tack!</Typography>
                        <Grid container>
                            Tack! Din betalning är nu genomförd. Du hittar dina bilder här nedanför. Ladda hem dem i full storlek genom att klicka på nedladdningsknappen vid varje bild.
                        </Grid>
                        {
                            Configuration.isFeatureEnabled('zipDownload')
                                ? <Grid container>
                                    Du kan också ladda ned alla bilder som en zip-fil genom att klicka på knappen:
                                    <Grid container>
                                        <Button
                                            onClick={downloadZipFile}
                                            disabled={zipFileIsLoading}
                                            variant='contained'>
                                            {zipFileDownloadButtonText}
                                        </Button>

                                    </Grid>
                                </Grid>
                                : <></>
                        }
                        <Grid container>
                            Du kan komma tillbaka till den här sidan för att hämta dina
                            bilder igen. Adressen är:
                        </Grid>

                        <Grid container className={classes.pageLinkFrame}>
                            <a href={window.location.href} title="Länk till den här sidan">{window.location.href}</a>
                        </Grid>
                        <Grid container>
                            Om du vill så kan du få länken skickad till dig i ett mail. Skriv i så fall din epost-adress här nedanför och klicka på "Skicka"-knappen.
                        </Grid>
                        <Grid size="auto">
                            Behöver du ett kvitto? <Link to={`/orders/${order.id}/receipt`}>Klicka här för kvitto som du kan spara eller skriva ut.</Link>
                        </Grid>

                        <Grid container>
                            <Grid container>
                                <TextField
                                    // defaultValue={localPricelist.name}
                                    variant='filled'
                                    label='Epost-adress'
                                    name='email'
                                    onChange={onEmailAddressChange}
                                />
                                <Button
                                    className={classes.sendMailButton}
                                    variant='contained'
                                    disabled={!emailIsValid}
                                    onClick={sendMail}>
                                    Skicka
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid container>
                        <Typography variant='h2'>Dina bilder</Typography>
                    </Grid>
                    <Grid container>
                        {
                            eventsWithPhotos.map((x) => (
                                <>
                                    <Grid container key={x.eventName}>
                                        <Typography variant='h3'>{x.eventName}</Typography>
                                    </Grid>
                                    <>
                                        {x.photos.map(y => <EventGalleryPhoto
                                            key={y.id}
                                            eventPhoto={y}
                                            isFreeOfCharge={true}
                                            loadLazy={false}
                                            isSelectedForDownload={false}
                                            onSelected={() => { }}
                                            onAddToShoppingCart={() => { }}
                                            downloadImage={() => downloadImage(y.eventId, y.id)}
                                            selectImageForDownload={() => Promise.resolve(undefined)}
                                        />
                                        )}
                                    </>
                                </>


                            ))

                        }
                    </Grid>

                </>

            )
            }
        </Root>
    );
};


const PREFIX = 'SwishPaymentIndexView';
const classes = {
    cartItemsContainer: `${PREFIX}-cartItemsContainer`,
    pageLinkFrame: `${PREFIX}-pageLinkFrame`,
    swishLogo: `${PREFIX}-swishLogo`,
    sendMailButton: `${PREFIX}-sendMailButton`,
}

const Root = styled('div')(({ theme }) => ({
    [`&.${classes.cartItemsContainer}`]: {
        minWidth: '400px'
    },
    [`&.${classes.pageLinkFrame}`]: {
        padding: '1rem'
    },
    [`&.${classes.swishLogo}`]: {
        width: '60px',
        marginRight: '1rem'
    },
    [`&.${classes.sendMailButton}`]: {
        marginLeft: '1rem !important'
    },
}));


export default OrdersPage;
